import { useQuery, useMutation, useQueryClient, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api } from '../api';
import { toast } from '../toast';
import type {
  ScheduleResponse,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  SessionResponse,
  CreateOneTimeSessionRequest,
  UpdateSessionRequest,
  UpdateSessionCoachRequest,
  MarkAttendanceRequest,
} from '@sportschool/shared-types';
import * as API from '../generated/sportSchool';

export function useSchedules(
  schoolId: string,
  filters?: {
    locationId?: string;
    coachMemberId?: string;
    dayOfWeek?: number;
    skillLevelId?: string;
    showOnlyPrivate?: boolean;
    showArchived?: boolean;
    query?: string;
    forStudent?: boolean;
  }
) {
  return useQuery<ScheduleResponse[]>({
    queryKey: ['schedules', schoolId, filters],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/schedules`, { params: filters });
      return res.data;
    },
    enabled: !!schoolId,
    placeholderData: keepPreviousData,
  });
}

export function useSchedule(schoolId: string, scheduleId: string, options?: any) {
  return useQuery<ScheduleResponse>({
    queryKey: ['schedules', schoolId, scheduleId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/schedules/${scheduleId}`);
      return res.data;
    },
    enabled: !!schoolId && !!scheduleId,
    ...options,
  });
}

export function useCreateSchedule(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateScheduleRequest) => {
      const res = await api.post(`/schools/${schoolId}/schedules`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules', schoolId] });
      toast.success('Grupa została pomyślnie utworzona');
    },
    onError: (data: any) => {
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się utworzyć grupy', code, args);
    },
  });
}

export function useDeleteSchedule(schoolId: string, scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/schools/${schoolId}/schedules/${scheduleId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules', schoolId] });
      toast.success('Schedule deleted');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to delete schedule', code, args);
    },
  });
}

export function useUpdateSchedule(schoolId: string, scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateScheduleRequest) => {
      const res = await api.put(`/schools/${schoolId}/schedules/${scheduleId}`, data);
      return res.data as ScheduleResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      toast.success('Schedule updated');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to update schedule', code, args);
    },
  });
}

export function useSetScheduleEnrollmentBlocked(schoolId: string, scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (isBlocked: boolean) => {
      const res = await api.post(`/schools/${schoolId}/schedules/${scheduleId}/enrollment-blocked`, { isBlocked });
      return res.data as ScheduleResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      toast.success('Ustawienia zapisów zostały zaktualizowane');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się zmienić blokady zapisów', code, args);
    },
  });
}

// ---- Sessions ----

export interface SessionFilters {
  skillLevelId?: string | string[];
  locationId?: string | string[];
  coachMemberId?: string | string[];
  from?: string;
  to?: string;
  query?: string;
  page?: number;
  pageSize?: number;
  sessionId?: string | string[];
  status?: string | number;
  sortDescending?: boolean;
  dayOfWeek?: number | number[];
  scheduleId?: string;
  forStudent?: boolean;
  includeCancelled?: boolean;
}

/** Query options of `useSessions` — shared with the offline prefetch so the cache keys cannot drift. */
export function sessionsQueryOptions(schoolId: string, filters?: SessionFilters) {
  return queryOptions({
    queryKey: ['sessions', schoolId, filters],
    queryFn: async (): Promise<SessionResponse[]> => {
      const res = await api.get(`/schools/${schoolId}/sessions`, { params: filters });
      return res.data;
    },
    enabled: !!schoolId,
  });
}

export function useSessions(schoolId: string, filters?: SessionFilters, options?: any) {
  return useQuery<SessionResponse[]>({
    ...sessionsQueryOptions(schoolId, filters),
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** Query options of `useSession` — shared with the offline prefetch. */
export function sessionQueryOptions(schoolId: string, sessionId: string) {
  return queryOptions({
    // Nested under ['sessions', schoolId] so session mutations invalidating that prefix also refresh the detail view.
    queryKey: ['sessions', schoolId, 'detail', sessionId],
    queryFn: async (): Promise<SessionResponse> => {
      const res = await api.get(`/schools/${schoolId}/sessions/${sessionId}`);
      return res.data;
    },
    enabled: !!schoolId && !!sessionId,
  });
}

export function useSession(schoolId: string, sessionId: string, options?: any) {
  return useQuery<SessionResponse>({
    ...sessionQueryOptions(schoolId, sessionId),
    ...options,
  });
}

export function useCreateSession(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateOneTimeSessionRequest) => {
      const res = await api.post(`/schools/${schoolId}/sessions`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      toast.success('Session created');
    },
    onError: () => toast.error('Failed to create session'),
  });
}

export function useUpdateSession(schoolId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateSessionRequest) => {
      const res = await api.put(`/schools/${schoolId}/sessions/${sessionId}`, data);
      return res.data as SessionResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      toast.success('Session updated');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Failed to update session', code, args);
    },
  });
}

export function useCancelSession(schoolId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason?: string) => {
      await api.post(`/schools/${schoolId}/sessions/${sessionId}/cancel`, { reason });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      toast.success('Session cancelled');
    },
    onError: () => toast.error('Failed to cancel session'),
  });
}

export function useReopenSession(schoolId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post(`/schools/${schoolId}/sessions/${sessionId}/reopen`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      toast.success('Session reopened');
    },
    onError: () => toast.error('Failed to reopen session'),
  });
}

export function useSummarizeSession(schoolId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { notes?: string; topic?: string }) => {
      await api.post(`/schools/${schoolId}/sessions/${sessionId}/summarize`, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      toast.success('Session completed');
    },
    onError: () => toast.error('Failed to complete session'),
  });
}

export function useUpdateSessionCoach(schoolId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateSessionCoachRequest) => {
      const res = await api.patch(`/schools/${schoolId}/sessions/${sessionId}/coach`, data);
      return res.data as SessionResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      toast.success('Session coach updated');
    },
    onError: () => toast.error('Failed to update session coach'),
  });
}

export function useSetSessionEnrollmentBlocked(schoolId: string, sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (isBlocked: boolean) => {
      const res = await api.post(`/schools/${schoolId}/sessions/${sessionId}/enrollment-blocked`, { isBlocked });
      return res.data as SessionResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      toast.success('Ustawienia zapisów zostały zaktualizowane');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const detail = data?.detail || data?.Detail;
      const code = data?.code || data?.Code;
      const args = data?.args || data?.Args;
      toast.error(detail || 'Nie udało się zmienić blokady zapisów', code, args);
    },
  });
}

export function useMarkAttendance(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: MarkAttendanceRequest) => {
      await api.post(`/schools/${schoolId}/attendance/mark`, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].includes(`/schools/${schoolId}/attendance`)
      });
      toast.success('Attendance marked');
    },
    onError: () => toast.error('Failed to mark attendance'),
  });
}

export function useDeleteAttendance(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enrollmentId: string) => {
      await api.delete(`/schools/${schoolId}/attendance/${enrollmentId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          query.queryKey[0].includes(`/schools/${schoolId}/attendance`)
      });
      toast.success('Wyczyściłeś obecność');
    },
    onError: () => toast.error('Nie udało się wyczyścić obecności'),
  });
}

export function useGetScheduleMessages(schoolId: string, scheduleId: string) {
  return useQuery<API.SessionMessageResponse[]>({
    queryKey: ['schedule-messages', schoolId, scheduleId],
    queryFn: async () => {
      const res = await api.get(`/schools/${schoolId}/schedules/${scheduleId}/messages`);
      return res.data;
    },
    enabled: !!(schoolId && scheduleId),
  });
}

export function useSendScheduleMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ schoolId, scheduleId, data }: { schoolId: string; scheduleId: string; data: { subject: string; body: string } }) => {
      const res = await api.post(`/schools/${schoolId}/schedules/${scheduleId}/messages`, data);
      return res.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['schedule-messages', variables.schoolId, variables.scheduleId] });
      qc.invalidateQueries({ queryKey: ['mailing-campaigns', variables.schoolId] });
    },
  });
}
